import * as React from 'react';
import PartStore from '../stores/PartStore';
import { buildSplitDocuments, splitDocumentName } from '../utils/splitPdf';
import { PDFDocument } from 'pdf-lib';
import { Document, Page } from 'react-pdf';
import { Row, Avatar, Card, Col, Button } from 'antd';
import {
    DeleteOutlined,
    BorderHorizontalOutlined,
    ScissorOutlined,
    ClearOutlined,
    CloudDownloadOutlined,
    CloseOutlined,
} from '@ant-design/icons';

interface Props {
    pdfFile: File;
    handleReset: () => void;
}

interface IPage {
    pageNumber: number;
    status: string;
}

const SplitPDF = (props: Props) => {
    const [totalPages, setTotalPages] = React.useState(Number);
    const [urlFile, setUrlFile] = React.useState<string>();
    const [fileName, setFileName] = React.useState<string>('');
    const [allPages, setAllPages] = React.useState<IPage[]>([]);
    const pageRef = React.useRef<any>();

    React.useEffect(() => {
        loadPDF();
    }, [props.pdfFile]);

    React.useEffect(() => {
        loadStructure();
    }, [totalPages]);

    async function loadPDF() {
        const buffer = props.pdfFile.arrayBuffer();
        const pdfDoc = await PDFDocument.load(await buffer);
        const pages_count = pdfDoc.getPageCount();

        setUrlFile(URL.createObjectURL(props.pdfFile));
        setTotalPages(pages_count);
        setFileName(props.pdfFile.name);
    }

    function loadStructure() {
        const initial_pages: IPage[] = [];
        [...Array(totalPages)].map((x, i) =>
            initial_pages.push({
                pageNumber: i + 1,
                status: 'active',
            })
        );
        setAllPages(initial_pages);
    }

    function updatePageStatus(page_number: number, status: string) {
        const updated_pages = allPages.map(obj => {
            if (obj.pageNumber === page_number) {
                const newStatus = status === obj.status ? 'active' : status;
                return { ...obj, status: newStatus };
            }
            return obj;
        });
        setAllPages(updated_pages);
    }

    function splitAllPages() {
        const updated_pages = allPages.map(obj => {
            return { ...obj, status: 'splitted' };
        });
        setAllPages(updated_pages);
    }

    function resetAllPages() {
        const updated_pages = allPages.map(obj => {
            return { ...obj, status: 'active' };
        });
        setAllPages(updated_pages);
    }

    async function exportDocuments() {
        const documents = buildSplitDocuments(allPages);
        const srcDoc = await PDFDocument.load(await props.pdfFile.arrayBuffer());

        // One at a time so the parts are added, and numbered, in page order.
        for (let index = 0; index < documents.length; index++) {
            const PDF = await PDFDocument.create();
            const copiedPages = await PDF.copyPages(srcDoc, documents[index].pages);

            copiedPages.forEach(page => PDF.addPage(page));
            const PDFFile = await PDF.save();
            PartStore.addPart(new File([PDFFile], splitDocumentName(index + 1, fileName), { type: 'application/pdf' }));
        }
        props.handleReset();
    }

    return (
        <div
            className="mb-2"
            style={{
                marginBottom: '5px',
                padding: '10px',
                border: '1px solid #e2e8f0',
            }}
        >
            <div>
                <Button
                    style={{ float: 'right' }}
                    onClick={() => props.handleReset()}
                    icon={<CloseOutlined />}
                    type="link"
                >
                    {' '}
                </Button>

                <p className="pb-2" style={{ textAlign: 'center', fontSize: '1.6rem', fontWeight: 600 }}>
                    Split PDF files
                </p>
                <p style={{ textAlign: 'center', color: 'rgba(0, 0, 0, 0.45)' }}>
                    Easy tool to split PDF files by pages.
                </p>
            </div>

            <div
                className="mb-2"
                style={{
                    marginBottom: '5px',
                    padding: '10px',
                }}
            >
                <Button onClick={() => resetAllPages()} icon={<ClearOutlined />} type="link">
                    Reset All
                </Button>
                <Button onClick={() => splitAllPages()} icon={<ScissorOutlined />} type="link">
                    Split All
                </Button>
                <Button
                    onClick={() => exportDocuments()}
                    icon={<CloudDownloadOutlined />}
                    type="primary"
                    style={{ float: 'right' }}
                >
                    Add Parts
                </Button>
            </div>

            <div
                className="main"
                style={{
                    marginBottom: '5px',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                }}
            >
                <Document file={urlFile}>
                    <>
                        <Row gutter={[16, 16]}>
                            {allPages.map(page => (
                                <>
                                    <Col className="gutter-row" span={5}>
                                        <Card
                                            {...(page.status === 'splitted'
                                                ? { width: 250, style: { borderRight: '5px dotted red' } }
                                                : { width: 250 })}
                                            cover={
                                                <div
                                                    ref={pageRef}
                                                    {...(page.status === 'deleted' ? { style: { opacity: '.2' } } : {})}
                                                >
                                                    <Page
                                                        width={pageRef.current ? pageRef.current.offsetWidth - 4 : 300}
                                                        height={pageRef.current ? pageRef.current.offsetHeight : 300}
                                                        pageNumber={page.pageNumber}
                                                    />
                                                </div>
                                            }
                                            actions={[
                                                <Avatar
                                                    {...(page.status != 'deleted'
                                                        ? { style: { backgroundColor: '#c00' } }
                                                        : {})}
                                                    gap={1}
                                                >
                                                    {page.pageNumber}
                                                </Avatar>,
                                                <DeleteOutlined
                                                    key="delete"
                                                    onClick={() => updatePageStatus(page.pageNumber, 'deleted')}
                                                    {...(page.status === 'deleted' ? { style: { color: '#c00' } } : {})}
                                                />,
                                                <BorderHorizontalOutlined
                                                    key="split"
                                                    onClick={() => updatePageStatus(page.pageNumber, 'splitted')}
                                                    {...(page.status === 'splitted'
                                                        ? { style: { color: '#c00' } }
                                                        : {})}
                                                />,
                                            ]}
                                        ></Card>
                                    </Col>
                                </>
                            ))}
                        </Row>
                    </>
                </Document>
            </div>
        </div>
    );
};

export default SplitPDF;
